/**
 * WZPrintTemplate.js
 *
 * Trzy kopie dokumentu WZ/RW na jednej stronie A4 (pionowo).
 * Kopie: ORYGINAŁ + KOPIA + KOPIA.
 *
 * Props data:
 *   numerWZ        : string
 *   typDokumentu   : "WZ" | "RW"
 *   egz            : { nazwa, adres, skrot }
 *   firma          : { nazwa, ulica, numer, kodpocztowy, miasto, bdo }
 *   odbiorca       : { nazwa }
 *   przeznaczenie  : string
 *   dataWystawienia: Date | string
 *   transport      : { nrRej }
 *   wydal          : string
 *   products       : [{ nazwaMaterialu: { nazwa } | string, ilosc }]
 */

import React, { forwardRef } from "react";
import "../css/WZPrint.css";

/* ------------------------------------------------------------------ */
const formatDate = (d) => {
  if (!d) return "";
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt)) return String(d);
  return dt.toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const getMaterialName = (prod) => {
  if (!prod?.nazwaMaterialu) return "";
  if (typeof prod.nazwaMaterialu === "string") {
    try { return JSON.parse(prod.nazwaMaterialu).nazwa ?? prod.nazwaMaterialu; }
    catch { return prod.nazwaMaterialu; }
  }
  return prod.nazwaMaterialu?.nazwa ?? "";
};

/* ------------------------------------------------------------------ */
/*  Pojedyncza kopia dokumentu                                         */
/* ------------------------------------------------------------------ */
const WZCopy = ({ data, kopiaLabel }) => {
  const {
    numerWZ = "",
    typDokumentu = "WZ",
    egz,
    firma,
    odbiorca,
    przeznaczenie = "",
    dataWystawienia,
    transport,
    wydal = "",
    products = [],
  } = data;

  const firmaNazwa    = firma?.nazwa ?? "";
  const firmaAdres    = [firma?.ulica, firma?.numer].filter(Boolean).join(" ");
  const firmaKodMiasto= [firma?.kodpocztowy, firma?.miasto].filter(Boolean).join(" ");
  const firmaBdo      = firma?.bdo ?? "";
  const firmaNip      = firma?.nip ?? "";
  const firmaRegon    = firma?.regon ?? "";

  const egzNazwa   = egz?.nazwa ?? egz?.adres ?? "";
  const dataStr    = formatDate(dataWystawienia);
  const transportNr = transport?.nrRej ?? "";

  // Tytuł zależny od typu dokumentu
  const docTitle = typDokumentu === "RW" ? "Rozchód Wewnętrzny" : "Wydanie Zewnętrzne";

  // Logo dynamiczne z bazy danych
  const firmaLogo = firma?.logo ?? null;

  // Zawsze min. 3 wiersze; produkty od góry, puste wiersze na dole.
  // Przy >3 produktach wiersze się zwężają (tabela ma stałą wysokość).
  const isSingleProduct = products.length === 1;
  const MIN_ROWS = 3;
  const rows = [...products];
  while (rows.length < MIN_ROWS) rows.push(null);
  const hasEmptyRows = products.length < MIN_ROWS;
  // indeks ostatniego prawdziwego wiersza (gdy są puste wiersze poniżej)
  const lastProductIdx = hasEmptyRows ? products.length - 1 : -1;
  

  return (
    <div className="wz-print-page">

      {/* NAGŁÓWEK */}
      <div className="wz-header">

        {/* Logo */}
        <div className="wz-logo-col">
          {firmaLogo && (
            <img src={firmaLogo} alt="Logo" className="wz-logo-img" />
          )}
        </div>

        {/* Dane firmy + tytul + odbiorca/przeznaczenie */}
        <div className="wz-company-col">
          <div className="wz-company-name">{firmaNazwa}</div>
          {firmaAdres    && <div className="wz-company-address">{firmaAdres}</div>}
          {firmaKodMiasto&& <div className="wz-company-address">{firmaKodMiasto}</div>}
          {(firmaNip || firmaRegon) && (
            <div className="wz-company-address">
              {firmaNip ? `NIP: ${firmaNip}` : ""}
              {firmaNip && firmaRegon ? "  " : ""}
              {firmaRegon ? `REGON: ${firmaRegon}` : ""}
            </div>
          )}
          <div className="wz-doc-title">{docTitle}&nbsp;&nbsp;{numerWZ}</div>

          <div className="wz-header-recipient-inline">
            <div className="wz-inline-group">
              <span className="wz-inline-label">Odbiorca:</span>
              <span className="wz-inline-value">{odbiorca?.nazwa ?? ""}</span>
            </div>
          </div>
        </div>

        {/* Egz. + data + BDO */}
        <div className="wz-meta-col">
          {egzNazwa && <div className="wz-egz-line">{egzNazwa}</div>}
          <div className="wz-date-line">{dataStr}</div>
          {firmaBdo && <div className="wz-bdo-line">Nr BDO: {firmaBdo}</div>}
          <div className="wz-original-badge">{kopiaLabel}</div>
        </div>
      </div>

      {/* TABELA TOWARÓW */}
      <table className={`wz-products-table${isSingleProduct ? " wz-products-table-single" : ""}`}>
        <thead>
          <tr>
            <th className="col-name">Nazwa Wyrobu:</th>
            <th className="col-qty">Ilość wydana</th>
            <th className="col-unit">Jedn.</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((prod, idx) =>
            prod ? (
              <tr key={idx} className={`wz-product-row${isSingleProduct ? " wz-product-row-featured" : ""}${hasEmptyRows && idx === lastProductIdx ? " wz-last-product-row" : ""}`}>
                <td className="col-name">{getMaterialName(prod)}</td>
                <td className="col-qty">
                  {prod.ilosc !== undefined && prod.ilosc !== null
                    ? String(prod.ilosc).replace(".", ",")
                    : ""}
                </td>
                <td className="col-unit">t</td>
              </tr>
            ) : (
              <tr key={idx} className="empty-row">
                <td className="col-name">&nbsp;</td>
                <td className="col-qty">&nbsp;</td>
                <td className="col-unit">&nbsp;</td>
              </tr>
            )
          )}
        </tbody>
      </table>

      {/* TRANSPORT */}
      <div className="wz-transport-row">
        <span className="wz-label">Śrd. Transportu:</span>
        <span className="wz-value">{transportNr}</span>
        <span className="wz-przezn-sep" />
        <span className="wz-label">Przeznaczenie:</span>
        <span className="wz-value">{przeznaczenie}</span>
      </div>

      {/* PODPISY */}
      <div className="wz-signatures">
        <div className="wz-sig-cell">
          <div className="wz-sig-label">Wydał:</div>
          <div className="wz-sig-line">{wydal}</div>
        </div>
        <div className="wz-sig-cell">
          <div className="wz-sig-label">Odebrałem oraz zapoznałem się z regulaminem załadunku</div>
          <div className="wz-sig-line">&nbsp;</div>
        </div>
        <div className="wz-sig-cell">
          <div className="wz-sig-label">Data dostawy</div>
          <div className="wz-sig-line">{dataStr}</div>
        </div>
        <div className="wz-sig-cell">
          <div className="wz-sig-label">Potwierdzam dostarczenie towaru</div>
          <div className="wz-sig-line">&nbsp;</div>
        </div>
      </div>

    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Wrapper – 3 kopie: ORYGINAŁ + KOPIA + KOPIA                       */
/* ------------------------------------------------------------------ */
const WZPrintTemplate = forwardRef(
  ({ data = {}, copyLabels = ["ORYGINAŁ"], className = "wz-copies-preview" }, ref) => {
    const copies = copyLabels.map((label) => ({ label }));

    return (
      <div className={className} ref={ref}>
        {copies.map(({ label }, idx) => (
          <WZCopy key={`${label}-${idx}`} data={data} kopiaLabel={label} />
        ))}
      </div>
    );
  }
);

WZPrintTemplate.displayName = "WZPrintTemplate";

export default WZPrintTemplate;
